import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../api/client';

export interface ModuleInfo {
  moduleId: string;
  displayName: string;
  dependencies: string[];
}

interface ModulesContextType {
  modules: ModuleInfo[];
  isModuleActive: (moduleId: string) => boolean;
  loading: boolean;
}

const ModulesContext = createContext<ModulesContextType>({
  modules: [],
  isModuleActive: () => true, // Default: all modules active (graceful degradation)
  loading: true,
});

export function ModuleProvider({ children }: { children: ReactNode }) {
  const [modules, setModules] = useState<ModuleInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadModules() {
      try {
        const data = await api.get<ModuleInfo[]>('/modules');
        if (!cancelled) {
          setModules(data);
        }
      } catch {
        // If module API fails, treat as all modules active (graceful degradation)
        // This ensures the app works even if the module endpoint is unavailable
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadModules();
    return () => { cancelled = true; };
  }, []);

  const isModuleActive = (moduleId: string): boolean => {
    // During loading or if API failed, allow all modules (graceful degradation)
    if (loading) return true;
    if (modules.length === 0) return true;
    return modules.some(m => m.moduleId === moduleId);
  };

  return (
    <ModulesContext.Provider value={{ modules, isModuleActive, loading }}>
      {children}
    </ModulesContext.Provider>
  );
}

export function useModules() {
  return useContext(ModulesContext);
}
